import { useContentAttachments } from '@/hooks/useContentAttachmentQueries';
import { useContentDetail } from '@/hooks/useContentQueries';
import { ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { useState } from 'react';

interface ProvenanceExpandableAttachmentsProps {
  parentContentId: number;
}

export function ProvenanceExpandableAttachments({
  parentContentId,
}: ProvenanceExpandableAttachmentsProps) {
  const attachmentsQuery = useContentAttachments(parentContentId);
  const [isExpanded, setIsExpanded] = useState(false);
  const attachmentIds = (attachmentsQuery.data?.items ?? []).map((item) => item.id);

  if (attachmentIds.length === 0) {
    return null;
  }

  const count = attachmentIds.length;
  const label = `${count} attachment${count === 1 ? '' : 's'}`;
  const Chevron = isExpanded ? ChevronUp : ChevronDown;

  return (
    <div className="pb-1">
      <button
        type="button"
        data-testid="chat-composer-attachments-toggle"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        className="flex h-7 w-full items-center gap-1.5 rounded-sm px-2 text-xs text-muted-foreground hover:bg-accent"
      >
        <Paperclip className="h-3 w-3" />
        <span className="flex-1 text-left">{label}</span>
        <Chevron className="h-3 w-3" />
      </button>
      {isExpanded ? (
        <div
          data-testid="chat-composer-attachments-list"
          className="mt-1 max-h-[8.75rem] overflow-y-auto scrollbar-thin border-t pt-1"
        >
          {attachmentIds.map((contentId) => (
            <ContentAttachmentRow key={contentId} contentId={contentId} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface ContentAttachmentRowProps {
  contentId: number;
}

function ContentAttachmentRow({ contentId }: ContentAttachmentRowProps) {
  const detail = useContentDetail(contentId);
  const titleCandidate =
    detail.data && 'title' in detail.data && typeof detail.data.title === 'string'
      ? detail.data.title.trim()
      : '';
  const label = titleCandidate.length > 0 ? titleCandidate : `Content #${contentId}`;

  return (
    <div
      data-testid="chat-composer-attachment-item"
      className="flex h-7 items-center gap-1.5 px-2 text-xs text-muted-foreground"
    >
      <Paperclip className="h-3 w-3 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}
